// Sprint UI-8 (H2) — theme store.
//
// Single source of truth for the dashboard's theme. Defaults to dark
// (the founder-first marathon-reading palette is dark-first; the light
// theme is on the roadmap but is not yet styled). On change the store
// flips the `dark` class on `<html>` so Tailwind's `dark:` variant
// resolves; persists the choice to `localStorage` so PWA reloads keep
// the same theme.
//
// SSR-safety: the store reads from `window.localStorage` at module
// init, but guards against undefined. The DOM mutation happens inside
// `apply()`, which is also a no-op outside the browser.

import { create } from 'zustand';

export type Theme = 'light' | 'dark';

export interface ThemeStore {
  theme: Theme;
  toggle: () => void;
  setTheme: (next: Theme) => void;
}

const STORAGE_KEY = 'as-oasis:theme';
const DEFAULT_THEME: Theme = 'dark';

function readInitial(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark') return raw;
  } catch {
    /* localStorage may be disabled (Safari private mode) */
  }
  return DEFAULT_THEME;
}

function apply(theme: Theme): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (theme === 'dark') root.classList.add('dark');
  else root.classList.remove('dark');
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    /* ignore quota / disabled */
  }
}

const initial = readInitial();
apply(initial);

export const useThemeStore = create<ThemeStore>((set, get) => ({
  theme: initial,
  toggle: () => {
    const next: Theme = get().theme === 'dark' ? 'light' : 'dark';
    apply(next);
    set({ theme: next });
  },
  setTheme: (next) => {
    apply(next);
    set({ theme: next });
  },
}));
